import { parseDateForStorage } from './importLogsParsingTime';
import { looksLikeCaregiverTimelineImportText } from './naturalCaptureChunkingCore';
import {
  CARE_DATE_MONTH_NAME_PATTERN,
  CARE_DATE_WEEKDAY_NAME_PATTERN,
  countCareExplicitTimes,
  createCareTimelineHeadingRegex,
} from '../../../shared/careDateTimePatterns';

const MONTH_NAME_PATTERN = CARE_DATE_MONTH_NAME_PATTERN;
const WEEKDAY_NAME_PATTERN = CARE_DATE_WEEKDAY_NAME_PATTERN;

const STRONG_TIMELINE_ANCHOR_PATTERN = createCareTimelineHeadingRegex({ global: true });
const MONTH_DAY_PATTERN = new RegExp(
  `\\b(?:(?:${WEEKDAY_NAME_PATTERN})(?:,)?\\s+)?${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?\\b`,
  'ig'
);
const INLINE_STATUS_DATE_PATTERN = new RegExp(
  `\\b(?:stopped|started|increased|decreased)\\s+(?:on\\s+)?${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?\\b`,
  'ig'
);

const getRowInspectionText = (row = {}) => [
  row?.title,
  row?.body,
  row?.note,
  row?.text,
  row?.content,
].map((value) => String(value || '').trim()).filter(Boolean).join(' ');

const countPatternMatches = (text = '', pattern) => Array.from(String(text || '').matchAll(pattern)).length;

const getStrongAnchorCount = (text = '') => countPatternMatches(text, STRONG_TIMELINE_ANCHOR_PATTERN);
const getMonthDayCount = (text = '') => countPatternMatches(text, MONTH_DAY_PATTERN);
const getInlineStatusDateCount = (text = '') => countPatternMatches(text, INLINE_STATUS_DATE_PATTERN);
const getExplicitTimeCount = (text = '') => countCareExplicitTimes(text);

const isSameCalendarDay = (left, right) => {
  const leftDate = parseDateForStorage(left);
  const rightDate = parseDateForStorage(right);
  if (!(leftDate instanceof Date) || Number.isNaN(leftDate.getTime())) return false;
  if (!(rightDate instanceof Date) || Number.isNaN(rightDate.getTime())) return false;

  return leftDate.getFullYear() === rightDate.getFullYear()
    && leftDate.getMonth() === rightDate.getMonth()
    && leftDate.getDate() === rightDate.getDate();
};

export const analyzeCaregiverTimelineImport = (text = '') => {
  const rawText = String(text || '').trim();
  const strongAnchorCount = getStrongAnchorCount(rawText);
  const monthDayCount = getMonthDayCount(rawText);
  const inlineStatusDateCount = getInlineStatusDateCount(rawText);
  const explicitTimeCount = getExplicitTimeCount(rawText);
  const looksLikeCaregiverTimelineImport = looksLikeCaregiverTimelineImportText(rawText)
    || strongAnchorCount >= 3
    || (strongAnchorCount >= 2 && inlineStatusDateCount >= 1)
    || (monthDayCount >= 3 && explicitTimeCount >= 2);

  return {
    looksLikeCaregiverTimelineImport,
    strongAnchorCount,
    monthDayCount,
    inlineStatusDateCount,
    explicitTimeCount,
  };
};

export const detectCollapsedTimelineParse = ({
  rawText = '',
  rows = [],
  fallbackDate = new Date(),
} = {}) => {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const {
    looksLikeCaregiverTimelineImport,
    strongAnchorCount,
    monthDayCount,
    inlineStatusDateCount,
    explicitTimeCount,
  } = analyzeCaregiverTimelineImport(rawText);

  const reasons = [];

  const forceTimelineRescueCandidate = (
    normalizedRows.length <= 2
    && monthDayCount >= 3
    && explicitTimeCount >= 2
  );

  if (!looksLikeCaregiverTimelineImport && !forceTimelineRescueCandidate) {
    return {
      shouldTrigger: false,
      looksLikeCaregiverTimelineImport,
      collapsedTimelineParse: false,
      reasons,
      anchorCount: strongAnchorCount,
      monthDayCount,
      inlineStatusDateCount,
      explicitTimeCount,
      localRowCount: normalizedRows.length,
    };
  }

  if (strongAnchorCount >= 3 && normalizedRows.length <= 2) {
    reasons.push('too_few_rows_for_anchor_count');
  }

  if (
    forceTimelineRescueCandidate
    && !reasons.includes('too_few_rows_for_repeated_date_times')
  ) {
    reasons.push('too_few_rows_for_repeated_date_times');
  }

  if (
    monthDayCount >= 4
    && explicitTimeCount >= 3
    && !reasons.includes('raw_text_contains_repeated_date_times')
  ) {
    reasons.push('raw_text_contains_repeated_date_times');
  }

  normalizedRows.forEach((row) => {
    const rowText = getRowInspectionText(row);
    if (!rowText) return;

    const rowAnchorCount = getStrongAnchorCount(rowText);
    const rowMonthDayCount = getMonthDayCount(rowText);
    const rowTimeCount = getExplicitTimeCount(rowText);

    if (
      rowAnchorCount >= 2
      && (rowTimeCount >= 2 || rowText.length >= 250)
      && !reasons.includes('row_contains_multiple_embedded_anchors')
    ) {
      reasons.push('row_contains_multiple_embedded_anchors');
    }

    if (
      rowTimeCount >= 2
      && rowText.length >= 180
      && !reasons.includes('row_contains_multiple_times')
    ) {
      reasons.push('row_contains_multiple_times');
    }

    if (
      rowMonthDayCount >= 2
      && rowTimeCount >= 2
      && rowText.length >= 140
      && !reasons.includes('row_contains_multiple_embedded_date_times')
    ) {
      reasons.push('row_contains_multiple_embedded_date_times');
    }

    if (
      rowText.length >= 350
      && rowTimeCount >= 2
      && (rowAnchorCount >= 2 || rowText.split(',').length >= 4)
      && !reasons.includes('giant_blob_with_multiple_times')
    ) {
      reasons.push('giant_blob_with_multiple_times');
    }

    if (
      rowAnchorCount >= 1
      && isSameCalendarDay(row?.date, fallbackDate)
      && !reasons.includes('fallback_date_survived_strong_anchor')
    ) {
      reasons.push('fallback_date_survived_strong_anchor');
    }
  });

  return {
    shouldTrigger: reasons.length > 0,
    looksLikeCaregiverTimelineImport,
    collapsedTimelineParse: reasons.length > 0,
    reasons,
    anchorCount: strongAnchorCount,
    monthDayCount,
    inlineStatusDateCount,
    explicitTimeCount,
    localRowCount: normalizedRows.length,
  };
};

export const shouldUseAITimelineSegmentation = (options = {}) => detectCollapsedTimelineParse(options);
